import { LEGACY_DOMAIN, RELOAD_TIMEOUT_MS, CSAT_URL } from "../shared/constants";
import { Roles } from "../shared/enums";
import { legacyHost, hubHost } from "./index";

const TOKEN_INFO_STORAGE_PREFIX = "televistoria:token-info:";
const tokenInfoMemoryCache = new Map<string, any>();

function canUseSessionStorage() {
  try {
    return typeof window !== "undefined" && !!window.sessionStorage;
  } catch (error) {
    return false;
  }
}

function getTokenStorageKey(token: string) {
  return `${TOKEN_INFO_STORAGE_PREFIX}${token}`;
}

function normalizeTokenInformation(data: any) {
  return Array.isArray(data) ? data[0] : data;
}

function cacheTokenInformation(token: string, data: any) {
  if (!token) return;

  const normalizedData = normalizeTokenInformation(data);

  if (normalizedData) {
    tokenInfoMemoryCache.set(token, normalizedData);
  }

  if (canUseSessionStorage()) {
    try {
      window.sessionStorage.setItem(getTokenStorageKey(token), JSON.stringify(normalizedData ?? null));
    } catch (error) {
      console.log(error);
    }
  }
}

export function getCachedTokenInformation(token?: string) {
  if (!token) return undefined;

  const memoryData = tokenInfoMemoryCache.get(token);
  if (memoryData) return memoryData;

  if (!canUseSessionStorage()) return undefined;

  try {
    const stored = window.sessionStorage.getItem(getTokenStorageKey(token));
    if (!stored) return undefined;

    const parsed = JSON.parse(stored);
    if (parsed) {
      tokenInfoMemoryCache.set(token, parsed);
    }

    return parsed ?? undefined;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getTokenInformation(token: string) {
  const response = await legacyHost.post(`/apiToken.cfm?action=getDadosCliente&token=${token}`);
  cacheTokenInformation(token, response.data);

  return response.data;
}

export async function saveEventHistory(message: string, token: string) {
  const payload = { texto: message };
  const response = await legacyHost.post(
    `/apiToken.cfm?action=registraHistorico&token=${token}`,
    payload,
    {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return response;
}

export async function getRemoteLogo(id: string) {
  let response;

  try {
    await legacyHost.get(`planetun-v2/sistema/includes/imagens/regulacoes/${id}/logo_topo.svg`);
    response = `${LEGACY_DOMAIN}/planetun-v2/sistema/includes/imagens/regulacoes/${id}/logo_topo.svg`;
  } catch (error) {
    console.log(error);
    await legacyHost.get(`planetun-v2/sistema/includes/imagens/regulacoes/${id}/logo_topo.png`);
    response = `${LEGACY_DOMAIN}/planetun-v2/sistema/includes/imagens/regulacoes/${id}/logo_topo.png`;
  }

  return response;
}

export async function completeToken(token: string) {
  const response = await legacyHost.get(
    `/apiToken.cfm?action=finalizaAtendimento&token=${token}&user=${Roles.Insured}`
  );
  return response;
}

export function redirectToMobi(token: string) {
  setTimeout(() => {
    window.parent.location.href = `${LEGACY_DOMAIN}/televistoria.cfm?token=${token}`;
  }, RELOAD_TIMEOUT_MS);
}

/**
 * Chave de sessionStorage onde persistimos o `csatReturnUrl` recebido no query param
 * da URL inicial. Necessária porque o app navega entre rotas internas (Welcome → Conference
 * → Feedback) e perde a query string original — sem persistir, o redirect ao CSAT
 * cairia no fallback do REACT_APP_CSAT_URL (BV legado).
 */
const CSAT_RETURN_URL_STORAGE_KEY = "televistoria:csatReturnUrl";

/**
 * Captura o `csatReturnUrl` da URL atual (se presente) e persiste em sessionStorage.
 * Chamar no entrypoint da sessão (Welcome) para que o override sobreviva às navegações
 * internas até o redirect final em redirectToCSAT.
 */
export function captureCsatReturnUrlFromQuery() {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("csatReturnUrl");
    if (fromQuery && typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem(CSAT_RETURN_URL_STORAGE_KEY, fromQuery);
    }
  } catch (error) {
    console.log(error);
  }
}

function readCsatOverride(): string | null {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("csatReturnUrl");
    if (fromQuery) return fromQuery;
    if (typeof window !== "undefined" && window.sessionStorage) {
      return window.sessionStorage.getItem(CSAT_RETURN_URL_STORAGE_KEY);
    }
  } catch (error) {
    console.log(error);
  }
  return null;
}

export function redirectToCSAT(insuranceId?: string, userId?: string) {
  const tag = Math.random().toString(36).slice(2);
  console.log("redirectToCSAT (entrada)", tag, { insuranceId, userId });

  const ins = insuranceId;       // captura no momento da chamada
  const usr = userId;
  const delay = Number(RELOAD_TIMEOUT_MS) || 0;

  // Override por query param OU sessionStorage: sistemas externos (ex.: novo Balcão
  // Virtual) podem passar `csatReturnUrl` na URL inicial. Persistimos em
  // sessionStorage no entrypoint (Welcome) porque navegações internas (PowerOffButton
  // → /feedback) limpam a query string. Quando presente, navega direto para a URL
  // fornecida — sem concatenar {ins}/{usr}. Permite cutover por jornada sem mudança
  // operacional simultânea em todas as seguradoras.
  const csatOverride = readCsatOverride();
  const base = (CSAT_URL || "").replace(/\/+$/, ""); // remove barra final

  setTimeout(() => {
    const url = csatOverride ?? `${base}/${ins}/${usr}`;
    console.log("redirectToCSAT (timeout)", tag, { ins, usr, override: !!csatOverride }, url);
    if (csatOverride) {
      window.parent.location.href = url;
    } else if (ins && usr) {
      window.parent.location.href = url;
    }
  }, delay);
}

export async function getVistoriaStatus(token: string) {
  // consulta a API do Mobi para verificar o status da sessão segurado/perito
  const response = await legacyHost.get(`/apiToken.cfm?action=status&token=${token}`);  
  
  const data = response.data;
  return Array.isArray(data) ? data[0] : data;
}

export async function registerInspectionHistory(inspectionData: {
  inspectionId: string;
  nameUser: string;
  message: string;
  companyPersonId?: string;
}) {
  try {
    const payload = {
      Id: crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}`,
      CompanyPersonId: inspectionData.companyPersonId,
      InspectionId: inspectionData.inspectionId,
      NameUser: inspectionData.nameUser,
      Message: inspectionData.message,
      IpUserLogged: 'client',
    };

    const response = await hubHost.post('/api/User/RegisterHubHistory', payload);
    console.log('Histórico registrado no Hub:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao registrar histórico Segurado balcão virtual no Hub:', error);
    // Não lança erro para não quebrar o fluxo
    return null;
  }
}
